// AGC: LotesContext — Compatibility wrapper around Zustand store + TanStack Query
// AGC: Preserves useLotes() hook API so all 20 consumers work unchanged
// AGC: Internal fetch/mutation logic delegates to services/lotesService.ts
// AGC: Offline pending changes queue kept here (Droidfarm-specific)

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef, useMemo } from 'react'
import { Plot } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useFarmStore } from '../services/farmService'
import { useLotesStore, usePlotsQuery, useAddPlotMutation, useArchivePlotMutation, useUnarchivePlotMutation } from '../services/lotesService'
import { useQueryClient } from '@tanstack/react-query'
import { generateFlowRunId, logFlowTrace } from '../utils/flowTrace'

// AGC: localStorage keys for offline queue
const PENDING_CHANGES_KEY = 'droidfarm_plots_pending_changes'

interface LotesContextType {
  plots: Plot[]
  archivedPlots: Plot[]
  allPlots: Plot[]
  isLoading: boolean
  isOnline: boolean
  isSyncing: boolean
  showArchived: boolean
  setShowArchived: (show: boolean) => void
  addPlot: (plot: Omit<Plot, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }) => Promise<void>
  updatePlot: (id: string, plot: Partial<Plot>) => Promise<void>
  deletePlot: (id: string) => Promise<void>
  archivePlot: (plotId: string, fieldId: string) => Promise<void>
  unarchivePlot: (plotId: string, fieldId: string) => Promise<void>
  getPlotById: (id: string) => Plot | undefined
  getActivePlotsForField: (fieldId: string) => Plot[]
  refreshPlots: () => Promise<void>
  syncWithFirebase: () => Promise<void>
}

const LotesContext = createContext<LotesContextType | undefined>(undefined)

interface LotesProviderProps {
  children: ReactNode
}

export const LotesProvider: React.FC<LotesProviderProps> = ({ children }) => {
  const { user, getAuthToken } = useAuth()
  const { activeFarmId } = useFarmStore()
  const { showArchived, setShowArchived: setShowArchivedStore } = useLotesStore()
  const qc = useQueryClient()
  const enabled = !!user?.uid

  // AGC: Query for all plots
  const { data: allPlotsRaw = [], isLoading: queryLoading, isFetching } = usePlotsQuery(enabled)

  // AGC: Mutations
  const addPlotMutation = useAddPlotMutation()
  const archiveMutation = useArchivePlotMutation()
  const unarchiveMutation = useUnarchivePlotMutation()

  // AGC: Online state
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine)
  useEffect(() => {
    const onLine = () => setIsOnlineState(true)
    const offLine = () => setIsOnlineState(false)
    window.addEventListener('online', onLine)
    window.addEventListener('offline', offLine)
    return () => {
      window.removeEventListener('online', onLine)
      window.removeEventListener('offline', offLine)
    }
  }, [])

  // AGC: Derived state — active and archived plots (deduplicated)
  const plots = useMemo(() => {
    const filtered = allPlotsRaw.filter((p) => !p.isArchived)
    return Array.from(new Map(filtered.map((p) => [p.id, p])).values())
  }, [allPlotsRaw])

  const archivedPlots = useMemo(() => {
    const filtered = allPlotsRaw.filter((p) => p.isArchived === true)
    return Array.from(new Map(filtered.map((p) => [p.id, p])).values())
  }, [allPlotsRaw])

  // AGC: Resolve field ID — prefer current farm from Zustand store
  const resolveFieldId = useCallback(async (): Promise<string | null> => {
    if (activeFarmId) return activeFarmId
    // AGC: Fallback to localStorage legacy key
    if (user?.uid) {
      const saved = localStorage.getItem(`droidfarm_currentFarm_${user.uid}`)
      if (saved) return saved
    }
    return null
  }, [activeFarmId, user?.uid])

  // AGC: Add plot with offline queue support
  const addPlot = useCallback(async (plotData: Omit<Plot, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }) => {
    if (!user?.uid) throw new Error('User must be authenticated to add plot')

    const flowRunId = generateFlowRunId()
    logFlowTrace(flowRunId, 'LOT_CREATE_START', { userId: user.uid, plotName: plotData.name })

    if (!plotData.fieldId) throw new Error('fieldId is required to add plot')

    // AGC: Resolve mock field IDs
    let targetFieldId = plotData.fieldId
    if (targetFieldId.startsWith('field-') || targetFieldId.startsWith('mock-')) {
      const resolved = await resolveFieldId()
      if (resolved) {
        targetFieldId = resolved
        plotData.fieldId = resolved
      }
    }

    const plotId = plotData.id || `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newPlot: Plot = {
      ...plotData,
      id: plotId,
      isArchived: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      fieldId: targetFieldId,
      farmId: plotData.farmId || targetFieldId,
    } as Plot

    // AGC: Optimistic update in Query cache
    qc.setQueryData<Plot[]>(['plots'], (old) => [...(old || []), newPlot])

    // AGC: Sync to server
    if (isOnlineState) {
      try {
        await addPlotMutation.mutateAsync(newPlot)
        logFlowTrace(flowRunId, 'LOT_SYNC_SUCCESS', { plotId })
      } catch {
        logFlowTrace(flowRunId, 'LOT_SYNC_FAILED', { plotId })
        addPendingChange('add', { data: newPlot, id: plotId })
      }
    } else {
      addPendingChange('add', { data: newPlot, id: plotId })
    }
  }, [user?.uid, isOnlineState, addPlotMutation, resolveFieldId, qc])

  // AGC: Update plot
  const updatePlot = useCallback(async (id: string, plotData: Partial<Plot>) => {
    // AGC: Optimistic update
    qc.setQueryData<Plot[]>(['plots'], (old) =>
      (old || []).map((p) => (p.id === id ? { ...p, ...plotData, updatedAt: new Date().toISOString() } : p)),
    )
    addPendingChange('update', { id, data: plotData })
  }, [qc])

  // AGC: Delete plot
  const deletePlot = useCallback(async (id: string) => {
    qc.setQueryData<Plot[]>(['plots'], (old) => (old || []).filter((p) => p.id !== id))
    addPendingChange('delete', { id })
  }, [qc])

  // AGC: Archive plot
  const archivePlot = useCallback(async (plotId: string, fieldId: string) => {
    let resolvedFieldId = fieldId
    if (!fieldId || fieldId.startsWith('field-') || fieldId.startsWith('mock-')) {
      const real = await resolveFieldId()
      if (real) resolvedFieldId = real
      else throw new Error('Cannot resolve field ID for archiving')
    }

    // AGC: Snapshot previous state so we can rollback if the API call fails.
    // AGC: Without this, a network/server failure leaves local state archived but server unarchived,
    // AGC: and the plot "mysteriously reappears" on next refresh.
    const previousPlots = qc.getQueryData<Plot[]>(['plots'])

    // AGC: Optimistic update
    qc.setQueryData<Plot[]>(['plots'], (old) =>
      (old || []).map((p) => (p.id === plotId ? { ...p, isArchived: true, archivedAt: new Date().toISOString() } : p)),
    )

    if (isOnlineState) {
      try {
        await archiveMutation.mutateAsync({ plotId, fieldId: resolvedFieldId })
      } catch (err) {
        // AGC: Rollback optimistic update so UI matches server state
        if (previousPlots) qc.setQueryData<Plot[]>(['plots'], previousPlots)
        throw err
      }
    }
  }, [resolveFieldId, isOnlineState, archiveMutation, qc])

  // AGC: Unarchive plot
  const unarchivePlot = useCallback(async (plotId: string, fieldId: string) => {
    let resolvedFieldId = fieldId
    if (!fieldId || fieldId.startsWith('field-') || fieldId.startsWith('mock-')) {
      const real = await resolveFieldId()
      if (real) resolvedFieldId = real
      else throw new Error('Cannot resolve field ID for unarchiving')
    }

    // AGC: Snapshot for rollback (same pattern as archivePlot)
    const previousPlots = qc.getQueryData<Plot[]>(['plots'])

    qc.setQueryData<Plot[]>(['plots'], (old) =>
      (old || []).map((p) => (p.id === plotId ? { ...p, isArchived: false, archivedAt: null } : p)),
    )

    if (isOnlineState) {
      try {
        await unarchiveMutation.mutateAsync({ plotId, fieldId: resolvedFieldId })
      } catch (err) {
        if (previousPlots) qc.setQueryData<Plot[]>(['plots'], previousPlots)
        throw err
      }
    }
  }, [resolveFieldId, isOnlineState, unarchiveMutation, qc])

  // AGC: Helpers
  const getPlotById = useCallback((id: string) => allPlotsRaw.find((p) => p.id === id), [allPlotsRaw])
  const getActivePlotsForField = useCallback(
    (fieldId: string) => allPlotsRaw.filter((p) => p.fieldId === fieldId && !p.isArchived),
    [allPlotsRaw],
  )

  const refreshPlots = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['plots'] })
  }, [qc])

  const syncWithFirebase = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['plots'] })
  }, [qc])

  // AGC: Offline pending changes queue (kept from original — needed for true offline support)
  const addPendingChange = useCallback((type: 'add' | 'update' | 'delete', data: Record<string, unknown>) => {
    if (!user?.uid) return
    try {
      const key = `${PENDING_CHANGES_KEY}_${user.uid}`
      const pendingStr = localStorage.getItem(key)
      const pending = pendingStr ? JSON.parse(pendingStr) : []
      pending.push({ type, ...data, timestamp: Date.now() })
      localStorage.setItem(key, JSON.stringify(pending))
    } catch { /* AGC: ignore storage errors */ }
  }, [user?.uid])

  // AGC: Listen for refresh-plots events (voice command triggers)
  useEffect(() => {
    const handler = () => { qc.invalidateQueries({ queryKey: ['plots'] }) }
    window.addEventListener('refresh-plots', handler)
    return () => window.removeEventListener('refresh-plots', handler)
  }, [qc])

  const value: LotesContextType = {
    plots,
    archivedPlots,
    allPlots: allPlotsRaw,
    isLoading: queryLoading,
    isOnline: isOnlineState,
    isSyncing: isFetching,
    showArchived,
    setShowArchived: setShowArchivedStore,
    addPlot,
    updatePlot,
    deletePlot,
    archivePlot,
    unarchivePlot,
    getPlotById,
    getActivePlotsForField,
    refreshPlots,
    syncWithFirebase,
  }

  return <LotesContext.Provider value={value}>{children}</LotesContext.Provider>
}

export const useLotes = (): LotesContextType => {
  const context = useContext(LotesContext)
  if (!context) throw new Error('AGC: useLotes must be used within LotesProvider')
  return context
}
